import { useTranslation } from 'react-i18next'

import {
  Button,
  CreditLetterIcon,
  EditIcon,
  Label,
  PinIcon,
  ProfileIcon,
  Select,
  Text,
} from '@mercai/clever'

import { DrawerBuyerProfile, SectionHeader } from '@/pages/business/components'
import { DrawerAddress } from '../drawer-address'

import { AddressProps, ProfileProps } from '@/types'
import { useMemo } from 'react'
import styles from './create-general-information.module.css'
import { CreateGeneralInformationProps } from './create-general-information.types'

export const CreateGeneralInformation = ({
  errors,
  setValues,
  address,
  buyerProfile,
  isComplete,
  nextStep,
  setQueryParams,
  queryParams,
  values,
}: CreateGeneralInformationProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const setBuyerProfile = (data?: ProfileProps) => {
    setValues((prev) => ({
      ...prev,
      buyerProfile: data,
      buyerProfileUuid: data?.uuid,
      ...(prev.buyerProfileUuid !== data?.uuid && {
        address: undefined,
        addressUuid: undefined,
      }),
    }))
  }

  const setAddress = (data?: AddressProps) => {
    setValues((prev) => ({
      ...prev,
      address: data,
      addressUuid: data?.uuid,
    }))
  }

  const onChangeValues = (key: string, value: string | number) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const supportedPaymentMethods = useMemo(() => {
    if (values?.paymentFormOption === 'credit') {
      return (
        buyerProfile?.financeProfile?.enabledPaymentMethods
          ?.filter((p) => p === 'mercai_cash' || p === 'external_credit')
          .map((payment) => ({
            label: tEnums(
              `financial_profile_transaction_payment_methods_${payment}`,
            ),
            value: payment,
          })) || []
      )
    }

    return (
      buyerProfile?.financeProfile?.enabledPaymentMethods?.map((payment) => ({
        label: tEnums(
          `financial_profile_transaction_payment_methods_${payment}`,
        ),
        value: payment,
      })) || []
    )
  }, [buyerProfile, values?.paymentFormOption])

  return (
    <SectionHeader
      title={`1. ${t('sectionGeneralInformationTitle')}`}
      key="general-information"
      isComplete={isComplete}
    >
      <div>
        <div className={styles['info-block']}>
          <div className={styles.title}>
            <ProfileIcon className={styles.icon} />
            <Text variant="title-medium">{t('buyerLabel')}</Text>
          </div>
          <div className={styles['info-row']}>
            {buyerProfile && (
              <div className={styles['info-row-content']}>
                <Text className={styles['info-item']}>
                  <strong>{t('companyLabel')}:</strong>{' '}
                  {buyerProfile?.companyName || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('emailLabel')}:</strong>{' '}
                  {buyerProfile?.email || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('customerLevelLabel')}:</strong>{' '}
                  {buyerProfile?.customerLevel?.slug
                    ? tEnums(
                        `profile_customer_${buyerProfile?.customerLevel?.slug.toLocaleLowerCase()}`,
                      )
                    : '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('phoneLabel')}:</strong>{' '}
                  {buyerProfile?.phone || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('originLabel')}:</strong>{' '}
                  {buyerProfile?.type
                    ? tEnums(`profile_type_${buyerProfile?.type}`)
                    : '-'}
                </Text>
              </div>
            )}

            {!buyerProfile && (
              <Text className={styles['info-row-content']}>
                {t('buyerInformationRFQLabel')}
              </Text>
            )}

            {buyerProfile ? (
              <Button
                variant="secondary"
                model="square"
                size="small"
                onClick={() => setQueryParams?.({ show: 'profile' })}
              >
                <EditIcon />
              </Button>
            ) : (
              <Button
                variant="light"
                onClick={() => setQueryParams?.({ show: 'profile' })}
              >
                {t('buyerAddLabel')}
              </Button>
            )}
          </div>
          {!!errors?.buyerProfileUuid && (
            <Text variant="text-base" className={styles.error}>
              {errors.buyerProfileUuid as string}
            </Text>
          )}
        </div>

        <div
          className={`${styles['info-block']} ${!buyerProfile ? styles.disabled : ''}`}
        >
          <div className={styles.title}>
            <PinIcon className={styles.icon} />
            <Text variant="title-medium">{t('deliveryLabel')}</Text>
          </div>
          <div className={styles['info-row']}>
            {address?.postalCode && (
              <div className={styles['info-row-content']}>
                <Text className={styles['info-item']}>
                  <strong>{t('postalCodeLabel')}:</strong>{' '}
                  {address?.postalCode || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('countryLabel')}:</strong>{' '}
                  {address?.country || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('stateLabel')}:</strong> {address?.state || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('cityLabel')}:</strong> {address?.city || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('neighborhoodLabel')}:</strong>{' '}
                  {address?.neighborhood || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('streetLabel')}:</strong> {address?.street || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('referencePointLabel')}:</strong>{' '}
                  {address?.reference || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('needsUnloadingSupportLabel')}:</strong>{' '}
                  {address?.needsUnloadingSupport
                    ? t('yesLabel')
                    : t('noLabel')}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('restrictedVehiclesLabel')}:</strong>{' '}
                  {address?.restrictedVehicles &&
                  address?.restrictedVehicles?.length > 0
                    ? address?.restrictedVehicles?.map((vehicle, i) => {
                        return `${i > 0 ? ',' : ''} ${tEnums(
                          `logistic_shipment_type_${vehicle}`,
                        )}`
                      })
                    : '-'}
                </Text>
              </div>
            )}

            {!address &&
              (!values?.deliveryOption ||
                values?.deliveryOption === 'receive_at_address') && (
                <Text className={styles['info-row-content']}>
                  {t('deliveryInformationLabel')}
                </Text>
              )}

            {values?.deliveryOption === 'pick_up' && (
              <Text className={styles['info-row-content']}>
                {t('deliveryPickUpInformationLabel')}
              </Text>
            )}

            {address?.postalCode || values?.deliveryOption === 'pick_up' ? (
              <Button
                variant="secondary"
                model="square"
                size="small"
                onClick={() => setQueryParams?.({ show: 'address' })}
              >
                <EditIcon />
              </Button>
            ) : (
              <Button
                variant="light"
                onClick={() => setQueryParams?.({ show: 'address' })}
              >
                {t('deliveryAddLabel')}
              </Button>
            )}
          </div>
          {!!errors?.addressUuid && (
            <Text variant="text-base" className={styles.error}>
              {errors.addressUuid as string}
            </Text>
          )}
        </div>

        <div
          className={`${styles['info-block']} ${!buyerProfile ? styles.disabled : ''}`}
        >
          <div className={styles.title}>
            <CreditLetterIcon className={styles.icon} />
            <Text variant="title-medium">
              {t('additionalInformationLabel')}
            </Text>
          </div>
          <div className={styles['info-row-content']}>
            <div>
              <Label className={styles.label}>
                {t('paymentConditionLabel')}
              </Label>
              <Select
                className={styles.select}
                value={values?.paymentFormOption}
                onChange={(value) => {
                  onChangeValues('paymentFormOption', value)
                }}
                options={[
                  { label: t('selectEmpty'), value: '' },
                  ...(buyerProfile?.financeProfile?.enabledPaymentForms?.map(
                    (form) => ({
                      label: tEnums(`financial_profile_payment_forms_${form}`),
                      value: form,
                    }),
                  ) || []),
                ]}
                placeholder={t('selectPlaceholder')}
                error={errors?.paymentFormOption as string}
              />
            </div>
            <div>
              <Label className={styles.label}>{t('paymentMethodLabel')}</Label>
              <Select
                className={styles.select}
                value={values?.paymentMethodOption}
                onChange={(value) => {
                  onChangeValues('paymentMethodOption', value)
                }}
                options={[
                  { label: t('selectEmpty'), value: '' },
                  ...supportedPaymentMethods,
                ]}
                placeholder={t('selectPlaceholder')}
                error={errors?.paymentMethodOption as string}
              />
            </div>
          </div>
        </div>

        <DrawerBuyerProfile
          isOpen={queryParams?.show === 'profile'}
          onHandleOpen={() => setQueryParams?.({ show: '' })}
          buyerProfile={buyerProfile}
          setBuyerProfile={
            setBuyerProfile as (data?: Partial<ProfileProps>) => void
          }
          onSubmit={() => setQueryParams?.({ show: 'address' })}
        />

        <DrawerAddress
          isOpen={queryParams?.show === 'address'}
          onHandleOpen={() => setQueryParams?.({ show: '' })}
          address={address}
          setAddress={setAddress as (data?: Partial<AddressProps>) => void}
          buyerProfileUuid={buyerProfile?.uuid || ''}
          onSubmit={nextStep}
          onBack={() => {
            setQueryParams?.({ show: 'profile' })
          }}
          deliveryOption={values?.deliveryOption}
          setDeliveryOption={(option) => {
            setValues((prev) => ({
              ...prev,
              deliveryOption: option,
            }))
          }}
        />
      </div>
    </SectionHeader>
  )
}
